import { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useData } from "../contexts/DataContext";
import { List, Columns3, CalendarDays, Plus, Search, FileSignature, Wallet } from "lucide-react";
import ListView from "../components/views/ListView";
import KanbanView from "../components/views/KanbanView";
import CalendarView from "../components/views/CalendarView";
import TaskDialog from "../components/TaskDialog";
import SpkSection from "../components/project/SpkSection";
import TermsSection from "../components/project/TermsSection";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { STATUS_META, PRIORITY_META } from "../components/ui-bits";
import { cn } from "../lib/utils";

export function useTaskFilters(tasks) {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [priority, setPriority] = useState("all");
  const [assignee, setAssignee] = useState("all");
  const [sort, setSort] = useState("manual");

  const filtered = useMemo(() => {
    let out = tasks;
    if (search.trim()) {
      const q = search.toLowerCase();
      out = out.filter((t) => t.title.toLowerCase().includes(q) || (t.description || "").toLowerCase().includes(q));
    }
    if (status !== "all") out = out.filter((t) => t.status === status);
    if (priority !== "all") out = out.filter((t) => t.priority === priority);
    if (assignee !== "all") out = out.filter((t) => (t.assignees || []).includes(assignee));
    if (sort === "due") out = [...out].sort((a, b) => (a.due_date || "9999").localeCompare(b.due_date || "9999"));
    else if (sort === "priority") {
      const rank = { urgent: 0, high: 1, normal: 2, low: 3 };
      out = [...out].sort((a, b) => rank[a.priority] - rank[b.priority]);
    }
    return out;
  }, [tasks, search, status, priority, assignee, sort]);

  return { search, setSearch, status, setStatus, priority, setPriority, assignee, setAssignee, sort, setSort, filtered };
}

export function FilterBar({ filters, users }) {
  const f = filters;
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="filter-bar">
      <div className="relative">
        <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input data-testid="filter-search" value={f.search} onChange={(e) => f.setSearch(e.target.value)} placeholder="Filter tasks…"
          className="h-8 w-44 pl-7 pr-2 text-sm rounded-md border border-border bg-transparent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary" />
      </div>
      <Select value={f.status} onValueChange={f.setStatus}>
        <SelectTrigger data-testid="filter-status" className="h-8 w-32 text-xs"><SelectValue placeholder="Status" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All statuses</SelectItem>
          {Object.entries(STATUS_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={f.priority} onValueChange={f.setPriority}>
        <SelectTrigger data-testid="filter-priority" className="h-8 w-32 text-xs"><SelectValue placeholder="Priority" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All priorities</SelectItem>
          {Object.entries(PRIORITY_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={f.assignee} onValueChange={f.setAssignee}>
        <SelectTrigger data-testid="filter-assignee" className="h-8 w-36 text-xs"><SelectValue placeholder="Assignee" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All assignees</SelectItem>
          {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={f.sort} onValueChange={f.setSort}>
        <SelectTrigger data-testid="filter-sort" className="h-8 w-32 text-xs"><SelectValue placeholder="Sort" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="manual">Manual order</SelectItem>
          <SelectItem value="due">Due date</SelectItem>
          <SelectItem value="priority">Priority</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

const VIEW_TABS = [
  { key: "list", label: "List", icon: List },
  { key: "board", label: "Board", icon: Columns3 },
  { key: "calendar", label: "Calendar", icon: CalendarDays },
  { key: "spk", label: "SPK", icon: FileSignature, hideFor: ["pelaksana"] },
  { key: "payments", label: "Payments", icon: Wallet, hideFor: ["pelaksana"] },
];

const TASK_VIEWS = ["list", "board", "calendar"];

export default function ProjectView() {
  const { projectId } = useParams();
  const { user } = useAuth();
  const { tasks, projects, users, setOpenTaskId } = useData();
  const [view, setView] = useState("list");
  const [dialog, setDialog] = useState(null); // {group?, task?, parentId?, dueDate?}
  const filters = useTaskFilters(tasks.filter((t) => t.project_id === projectId));
  const visibleTabs = VIEW_TABS.filter((t) => !(t.hideFor || []).includes(user?.role));

  const project = projects.find((p) => p.id === projectId);
  if (!project) return <div className="p-6 text-sm text-muted-foreground" data-testid="project-not-found">Project not found.</div>;

  const topLevel = filters.filtered.filter((t) => !t.parent_id);
  const groups = Object.entries(STATUS_META).map(([key, m]) => ({
    key, label: m.label, color: m.color, projectId, status: key,
    tasks: topLevel.filter((t) => t.status === key),
  }));

  return (
    <div className="p-4 md:p-6 h-full flex flex-col" data-testid="project-view">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex gap-1 border-b border-border overflow-x-auto max-w-full" data-testid="view-tabs-bar">
          {visibleTabs.map(({ key, label, icon: Icon }) => (
            <button key={key} data-testid={`view-tab-${key}`} onClick={() => setView(key)}
              className={cn("flex items-center gap-1.5 px-3 py-2 text-sm border-b-2 -mb-px transition-colors whitespace-nowrap shrink-0",
                view === key ? "text-foreground border-[#E31E24] font-medium" : "text-muted-foreground border-transparent hover:text-foreground")}>
              <Icon size={15} />{label}
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2">
          {TASK_VIEWS.includes(view) && <FilterBar filters={filters} users={users} />}
          {TASK_VIEWS.includes(view) && (
            <button data-testid="add-task-button" onClick={() => setDialog({ group: { projectId, status: "todo" } })}
              className="h-8 px-3 flex items-center gap-1.5 text-sm font-medium rounded-md bg-[#E31E24] hover:bg-[#C81A1F] text-white transition-colors">
              <Plus size={15} />Add Task
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        {view === "list" && (
          <ListView groups={groups} users={users}
            onOpenTask={(t) => setOpenTaskId(t.id)}
            onEditTask={(t) => setDialog({ task: t })}
            onAddTask={(g) => setDialog({ group: g })}
            onAddSubtask={(t) => setDialog({ parentId: t.id, group: { projectId } })} />
        )}
        {view === "board" && (
          <KanbanView tasks={filters.filtered} users={users}
            onOpenTask={(t) => setOpenTaskId(t.id)}
            onAddTask={(g) => setDialog({ group: { ...g, projectId } })} />
        )}
        {view === "calendar" && (
          <CalendarView tasks={filters.filtered}
            onOpenTask={(t) => setOpenTaskId(t.id)}
            onAddTask={(dateKey) => setDialog({ group: { projectId, status: "todo" }, dueDate: dateKey })} />
        )}
        {view === "spk" && <SpkSection project={project} />}
        {view === "payments" && <TermsSection project={project} />}
      </div>

      {dialog && (
        <TaskDialog open onClose={() => setDialog(null)}
          projectId={dialog.task?.project_id || dialog.group?.projectId || projectId}
          parentId={dialog.parentId || null}
          task={dialog.task || null}
          defaultStatus={dialog.group?.status || "todo"}
          defaultDueDate={dialog.dueDate || null} />
      )}
    </div>
  );
}
